import { getSponsorsGroupsPage } from 'api/operations/groupService';
import { GROUPS_FILTERS_TITLES } from 'global/utils/DashboardFilters';
import { makeAutoObservable, runInAction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { GroupFilter, GroupSortBy, SortOrder, BiDashboardFilter } from 'types/groupService';
import { createContext, useContext } from 'react';
import { getEnrollmentPeriodsByGroupIds } from 'api/operations/enrollmentService';
import { mapEnrollmentsToGroups } from 'global/services-helpers/enrollmentService';
import { getOrgAffiliation } from 'api/operations/aaaService';
import { FullAgency } from 'types/aaaService';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { PaginationDelegate } from 'store/general/paginationDelegate';
import { BiDashboardGroupDelegate } from './biDashboardGroupDelegate';
import { GroupInteractionDelegate } from './groupInteractionDelegate';
import { SearchAgencyDelegate } from './searchAgencyDelegate';

export type BiReportFileType =
  | 'BI_REPORT_GROUP'
  | 'BI_REPORT_QUOTING'
  | 'BI_REPORT_RENEWAL'
  | 'AGENCY_REPORT'
  | 'AGENT_REPORT'
  | 'USER_REPORT'
  | 'AGENCY_AGENT_REPORT'
  | 'AGENCY_USER_REPORT';

export class BiDashboardPageStore {
  private _rootStore: RootStore = getRootStoreInstance();
  private _isLoading: boolean = false;
  private _groups: BiDashboardGroupDelegate[] = [];

  public paginationDelegate = new PaginationDelegate<GroupSortBy, SortOrder, BiDashboardFilter>({
    defaultSortBy: GroupSortBy.UPDATE_TIME,
    defaultSortOrder: SortOrder.DESC,
    filters: {
      filter: GroupFilter.ALL,
      name: '',
      effectiveDate: '',
      agencyId: '',
      fullView: true,
      assignedGroups: false,
      createdByMe: false,
      state: '',
      carrierGroupNumber: '',
    },
  });

  private _allSponsorAgencies: FullAgency[] = [];
  private _filterAllGroupsAndConnections = true;

  public groupInteractionDelegate = new GroupInteractionDelegate({
    setPageLoading: (status: boolean): void => this.setLoading(status),
    updatePageData: (): Promise<void> => this.getInitialData(),
    getAllSponsorAgencies: (): FullAgency[] => this._allSponsorAgencies,
  });

  public searchAgencyDelegate = new SearchAgencyDelegate({
    getAllSponsorAgencies: (): FullAgency[] => this._allSponsorAgencies,
  });

  public get groupFilters(): GroupFilter[] {
    return (
      this._rootStore.domainStoreV2.domainHeader.properties?.availablePipelineStatuses ||
      (Object.keys(GROUPS_FILTERS_TITLES) as GroupFilter[])
    );
  }

  public get groups(): BiDashboardGroupDelegate[] {
    return this._groups;
  }

  public get filterAllGroupsAndConnections(): boolean {
    return this._filterAllGroupsAndConnections;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get allSponsorAgencies(): FullAgency[] {
    return this._allSponsorAgencies;
  }

  public get availableStatesOptions(): OptionType[] {
    const availableStates = this._rootStore.domainStoreV2.domainHeader.properties.eligibleStates;
    return [
      { value: '', label: 'All' },
      ...availableStates.map((state) => ({
        value: state,
        label: state,
      })),
    ];
  }

  public setLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getInitialData(): Promise<void> {
    this.setLoading(true);
    const filters = this.paginationDelegate.filters;
    const groups = await getSponsorsGroupsPage({
      page: this.paginationDelegate.page,
      sortBy: this.paginationDelegate.sortBy,
      sortOrder: this.paginationDelegate.sortOrder,
      ...filters,
    });

    await runInAction(async () => {
      this.paginationDelegate.setTotalPages(groups.totalPages);
      this.paginationDelegate.setTotalElements(groups.totalElements);

      const enrollments = await getEnrollmentPeriodsByGroupIds(
        groups.result.map((group) => group.id),
        this.paginationDelegate.filters.filter === GroupFilter.SOLD,
      );

      runInAction(() => {
        this._groups = groups.result.map(
          (group) =>
            new BiDashboardGroupDelegate({
              group,
              groupInteractionDelegate: this.groupInteractionDelegate,
              enrollments: mapEnrollmentsToGroups(enrollments)[group.id],
            }),
        );
      });
      this.setLoading(false);
    });
  }

  public async getAllAgencies(): Promise<void> {
    this.setLoading(true);

    const allAgencies = await getOrgAffiliation();
    runInAction(() => {
      this._allSponsorAgencies = allAgencies.filter((agency) =>
        agency.sponsors.some((agencySponsor) => agencySponsor.id === this._rootStore.userStore.sponsorId),
      );
      this.setLoading(false);
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const BiDashboardPageStoreContext = createContext(new BiDashboardPageStore());

const useBiDashboardPageStore = (): BiDashboardPageStore => {
  return useContext(BiDashboardPageStoreContext);
};

export { BiDashboardPageStoreContext, useBiDashboardPageStore };
